import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { makeStyles, withStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  Grid,
  Tooltip,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { InfoOutlined, Check, Report } from '@material-ui/icons';

import axios from 'src/utils/axios';
import { displayErrorMessage } from 'src/utils/messages';
import { useToasts } from 'src/utils/Toast';
import Buk from '../../assets/logo/logo-buk.png';
import Talana from '../../assets/logo/logo-talana.png';
import Skualo from '../../assets/logo/logo-skualo.png';
import { setNextIntegration } from 'src/actions/company/companyActions';

const useStyles = makeStyles(theme => ({
  root: {
    padding: '14px'
  },
  cardInfo: {
    marginBottom: '5px',
    display: 'flex'
  },
  card: {
    background: 'linear-gradient(197deg, #415cc6, #20358c)',
    maxWidth: 600
  },
  toolTipTitle: {
    margin: '10px',
    color: 'white'
  },
  toolTipText: {
    color: 'white'
  },
  toolTipTextValue: {
    marginLeft: '10px',
    color: 'white',
    fontWeight: 600
  },
  integrationIcon: {
    margin: 'auto'
  },
  integrationTitle: {
    color: theme.palette.primary.main
  },
  listItem: {
    marginTop: '15px'
  }
}));

const HtmlTooltip = withStyles(() => ({
  tooltip: {
    backgroundImage: 'linear-gradient(197deg, #415cc6, #20358c)',
    maxWidth: 600,
    border: '0px'
  }
}))(Tooltip);

const IntegrationsList = ({ integrationSettings, orientation }) => {
  const classes = useStyles();

  return (
    <Grid style={{ margin: 'auto' }}>
      <HtmlTooltip
        placement={orientation}
        title={
          <React.Fragment>
            <Card className={classes.card}>
              <CardContent>
                <Typography
                  className={classes.toolTipTitle}
                  variant="h3"
                  align="center"
                >
                  Cuentas con las siguientes integraciones:
                </Typography>
                <Grid style={{ margin: 'auto' }}>
                  {Object.keys(integrationSettings).length ? (
                    Object.keys(integrationSettings).map((setting, x) => (
                      <Grid item key={x} className={classes.listItem}>
                        {integrationSettings[setting].translate instanceof
                        Array ? (
                          <>
                            {integrationSettings[setting]?.translate.map(
                              (data, y) => (
                                <Typography
                                  key={y}
                                  className={classes.toolTipText}
                                >
                                  {parse(data)}
                                </Typography>
                              )
                            )}
                          </>
                        ) : (
                          <>
                            <Typography className={classes.toolTipText}>
                              {parse(integrationSettings[setting].translate)}
                              <strong className={classes.toolTipTextValue}>
                                {integrationSettings[setting].content &&
                                  integrationSettings[setting].content}
                              </strong>
                            </Typography>
                          </>
                        )}
                      </Grid>
                    ))
                  ) : (
                    <Typography align="center" className={classes.toolTipText}>
                      No se encontraron integraciones
                    </Typography>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </React.Fragment>
        }
      >
        <InfoOutlined fontSize="large" color="primary" />
      </HtmlTooltip>
    </Grid>
  );
};

function IntegrationStatus({ integrationsStatus }) {
  const classes = useStyles();

  const localTimestamp = timestamp => moment(timestamp).format('HH:mm');

  return (
    <>
      <Grid className={classes.cardInfo}>
        {integrationsStatus?.status === 'FINISHED' ? (
          <>
            <Typography variant="h4" className={classes.integrationTitle}>
              Integración : Activa
            </Typography>
            <Check
              style={{ fill: '#89b832' }}
              className={classes.integrationIcon}
            />
          </>
        ) : integrationsStatus?.status === 'ERROR' ? (
          <>
            <Typography variant="h4">Integración : Error</Typography>
            <Report
              style={{ fill: 'red' }}
              className={classes.integrationIcon}
            />
          </>
        ) : (
          integrationsStatus?.status === 'RUNNING' && (
            <>
              <Typography variant="h4">Integración : Corriendo</Typography>
              <Report
                style={{ fill: 'blue' }}
                className={classes.integrationIcon}
              />
            </>
          )
        )}
      </Grid>
      {integrationsStatus.startTime === null ? (
        <Typography align="center">
          {'Última actualización: No disponible'}
        </Typography>
      ) : (
        <Typography align="center">{`Última actualización: 
      ${moment(integrationsStatus.startTime).format('HH:mm')}`}</Typography>
      )}
      {integrationsStatus.nextIntegration && (
        <Typography align="center">{`Próxima actualización: ${localTimestamp(
          integrationsStatus.nextIntegration
        )}
      `}</Typography>
      )}
    </>
  );
}

function IntegrationSummary({ className, orientation }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [integrationsStatus, setIntegrationsStatus] = useState({});
  const { error: errorToast } = useToasts();
  const { currentCompany, integrationSettings } = useSelector(
    state => state.company
  );

  const fetchIntegrationStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}company/integration-execution/last`
      );
      const { data } = response.data;
      setIntegrationsStatus(data);
      setLoading(false);
      dispatch(setNextIntegration(data.nextIntegration));
    } catch (e) {
      const requestType = 'GET';
      const message = 'Something went wrong.';

      displayErrorMessage(errorToast)(e, requestType, message);
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchIntegrationStatus();
  }, [fetchIntegrationStatus]);

  return (
    <Card className={clsx(classes.root, className)}>
      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container alignItems="center" justify="space-between" spacing={5}>
          <Grid item>
            <IntegrationsList
              integrationSettings={integrationSettings}
              orientation={orientation}
            />
          </Grid>
          <Grid item>
            <IntegrationStatus
              currentCompany={currentCompany}
              integrationsStatus={integrationsStatus}
            />
          </Grid>
          <Grid item>
            {currentCompany?.integrationType === 'buk' ? (
              <img src={Buk} style={{ width: '60px' }} alt="buk" />
            ) : currentCompany?.integrationType === 'talana' ? (
              <img src={Talana} style={{ width: '60px' }} alt="talana" />
            ) : currentCompany?.integrationType === 'skualo' ? (
              <img src={Skualo} style={{ width: '60px' }} alt="skualo" />
            ) : (
              <>
                <Typography>Integración con:</Typography>
                <Typography align="center">
                  {currentCompany?.integrationType}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
      )}
    </Card>
  );
}

IntegrationSummary.propTypes = {
  className: PropTypes.string,
  orientation: PropTypes.string
};

export default IntegrationSummary;
